/**
 * What the editor says about a template's files: that they are not in the
 * preview, and — slice 23's third done-when — **which of the two ways they will
 * travel**.
 *
 * ## WHY THIS IS ITS OWN MODULE AND NOT A BLOCK INSIDE `AttachmentsPanel`
 *
 * The panel imports the API client, which reaches `lib/config.ts`, which reads
 * `import.meta.env` — so a verify suite cannot render it at all. Everything it
 * says about the delivery mode was therefore provable only as *source text*: an
 * `includes` over the file, which cannot tell a sentence that is in the file
 * from a sentence that reaches the screen. A summary behind a condition that is
 * never true, or one left inside a disclosure nobody opens, passes that check
 * exactly as well as a working one does.
 *
 * This component is pure copy and `@wend/ui` primitives — no client, no
 * context, no effects — so `verify-template-images` renders it in both modes and
 * reads the words out of the markup. It is the same extraction SLICES records
 * as slice 24 item 17 for the campaign results panel, done here because slice
 * 23's done-when is precisely "the editor **says**".
 *
 * ## WHAT IT MAY AND MAY NOT CLAIM
 *
 * The three sentences are ordered by what a reader gets wrong without them:
 *
 *   1. **the preview one.** A file somebody uploaded and then cannot find in the
 *      preview reads as a bug. This is the claim that replaced a false one — the
 *      panel used to say attachments could not be sent at all, long after they
 *      could — and it stays at the top, in the slot that mistake occupied;
 *   2. **the mode summary**, in the body rather than behind the disclosure,
 *      because it is the line that differs between two deployments and a person
 *      who reads one line should read that one;
 *   3. **the mode detail and the size arithmetic**, behind `MoreDetail`. The
 *      detail is what the mode costs the reader — no offline copy, a link when
 *      the mail is forwarded, or a file copied per recipient — and it is one
 *      click from the summary rather than a paragraph everybody scrolls past.
 *
 * The per-file ceiling is deliberately NOT repeated here. `AttachmentsPanel`
 * prints it once, through `formatFileSize(MAX_ATTACHMENT_BYTES)`, and a second
 * copy on the same card — formatted by different code — is the retyped number
 * `verify-template-images` sweeps for: the two drift the moment the constant
 * moves, and the screen then shows one limit twice with two values.
 *
 * The size clause is keyed on `imageDelivery` and not on the attachment mode,
 * because it is about what counts towards Amazon's message limit and the two
 * kinds leave the message together. Told wrongly, a person deletes a picture to
 * make room for a PDF, or attaches one beside a half-megabyte banner.
 *
 * A null mode — a template that is not email, which never renders this — says
 * neither. Guessing would put the wrong one on screen, and the wrong one here is
 * a promise about somebody else's mail.
 */
import {
  TEMPLATE_ATTACHMENT_DELIVERY_DETAIL,
  TEMPLATE_ATTACHMENT_DELIVERY_SUMMARY,
  attachmentDeliveryFor,
  type TemplateImageDelivery,
} from '@wend/shared';
import { Callout } from '@wend/ui';

import { MoreDetail } from './MoreDetail';

export interface AttachmentDeliveryNoticeProps {
  /**
   * How this deployment carries pictures, from the template view. The file mode
   * is derived from it — `attachmentDeliveryFor` — never passed separately: one
   * host is configured or none is, and a screen that decided for itself is how
   * the editor comes to promise one thing while the send path does another.
   */
  imageDelivery: TemplateImageDelivery | null;
}

export function AttachmentDeliveryNotice({ imageDelivery }: AttachmentDeliveryNoticeProps) {
  const delivery = imageDelivery === null ? null : attachmentDeliveryFor(imageDelivery);

  return (
    <Callout tone="info" title="Attachments do not appear in the preview">
      {/*
        The reason differs by mode and both are true statements about the same
        observation. Attached: the file is not in the body at all, and the
        preview renders the body. Linked: the links ARE in the body — that is
        what `withAttachmentSection` puts there — but they are added when the
        message is sent, and the preview renders the stored document. Saying the
        attached reason on a linking deployment is the right observation with the
        wrong explanation, which is worse than saying nothing: it teaches a
        person that the links are not part of the email.
      */}
      {delivery === 'linked'
        ? 'The links are added when the message is sent, and the preview renders the template as it is stored.'
        : 'They are not part of the email body — the preview renders the body.'}
      {delivery !== null && (
        <p className="mt-1 font-medium">{TEMPLATE_ATTACHMENT_DELIVERY_SUMMARY[delivery]}.</p>
      )}
      {/*
        "HOW THEY ARE SENT" WAS 120 WORDS OF INFRASTRUCTURE — SLICE 29.

        Raw MIME parts, Amazon’s own message-size limit, and whether the pictures
        in the body are fetched from Wend or travel inside every copy of the
        message. It was the longest folded block on the template screens, and
        every sentence in it was about how this deployment builds a message — on
        the surface where somebody is writing one.

        **The budget stays, above.** A writer can act on that: it is the number
        that decides whether this file can go. How the budget is computed, and
        what else counts towards it, is the operator’s and belongs with the
        deployment’s own description.

        Folding it never made it sender content; it only hid the word count
        while leaving the summary line on screen permanently.
      */}
    </Callout>
  );
}

